import React, { useEffect, useState } from 'react';
import { api } from './api';
import { t } from './i18n';

const COLORS = {
  primary: '#1a4d8f', text: '#1f2937', muted: '#6b7280', border: '#e5e7eb', bg: '#f7f8fa',
};

// `recommended_by` is a comma-separated list and the filter matches WHOLE
// tokens (TAG_SPLIT_SQL in routes/categories.js), so a place joins a list by
// carrying that list's name character for character. Typing "Kosharot list"
// or "kosharot," creates a second, empty list rather than joining the real
// one — which is the whole reason this is a tick-box over the names already
// in use instead of a free-text box.
const SPLIT = /\s*[,;]\s*/;
export const splitTags = (s) => String(s || '').split(SPLIT).map((x) => x.trim()).filter(Boolean);

// Admin-only control for "who vouches for this place". Lists every
// recommendation list in use, site-wide and unscoped: the point of the
// control is to put a Rome place on Kosharot's list, so scoping the options
// to the place's own city would hide exactly the name being reached for.
export default function RecommendedByPicker({ value, onChange }) {
  const [known, setKnown] = useState([]);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    let live = true;
    api.recommenders()
      .then((r) => { if (live) setKnown(Array.isArray(r) ? r : []); })
      .catch(() => { if (live) setKnown([]); });
    return () => { live = false; };
  }, []);

  const picked = splitTags(value);
  const lower = picked.map((p) => p.toLowerCase());

  // Every name in use, plus anything this place already carries that the
  // list does not have — a recommender first used on a place still pending
  // approval is not in /recommenders yet, and must not silently vanish from
  // the form the moment an admin opens it.
  const options = [...known.map((k) => ({ name: k.value, count: k.count }))];
  for (const p of picked) {
    if (!options.some((o) => o.name.toLowerCase() === p.toLowerCase())) options.push({ name: p, count: null });
  }

  const write = (list) => onChange(list.join(', '));

  const toggle = (name) => {
    write(lower.includes(name.toLowerCase())
      ? picked.filter((p) => p.toLowerCase() !== name.toLowerCase())
      : [...picked, name]);
  };

  const addDraft = () => {
    const n = draft.trim().replace(/[,;]+$/, '').trim();
    if (!n) return;
    if (!lower.includes(n.toLowerCase())) write([...picked, n]);
    setDraft('');
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o) => {
          const on = lower.includes(o.name.toLowerCase());
          return (
            <label
              key={o.name}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                padding: '6px 10px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                background: on ? '#eaf1fa' : 'white',
                color: on ? COLORS.primary : COLORS.text,
              }}
            >
              <input type="checkbox" checked={on} onChange={() => toggle(o.name)} style={{ margin: 0 }} />
              {o.name}
              {o.count != null && <span style={{ color: COLORS.muted, fontWeight: 500 }}>({o.count})</span>}
            </label>
          );
        })}
        {options.length === 0 && (
          <div style={{ fontSize: 13, color: COLORS.muted }}>{t('No recommendation lists yet — start one below.')}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          style={{
            flex: 1, padding: '8px 10px', border: `1px solid ${COLORS.border}`,
            borderRadius: 8, fontSize: 14,
          }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
          placeholder={t('Start a new list — e.g. Rabbi Moshe Zalmanov')}
        />
        <button
          type="button"
          onClick={addDraft}
          disabled={!draft.trim()}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
            border: `1px solid ${COLORS.primary}`,
            background: draft.trim() ? COLORS.primary : COLORS.bg,
            color: draft.trim() ? 'white' : COLORS.muted,
            cursor: draft.trim() ? 'pointer' : 'default',
          }}
        >
          {t('Add')}
        </button>
      </div>
    </div>
  );
}
